<template>
  <div>
    <b-modal
      v-model="isShown"
      :title="$t('views.shipment_notice_list.create_asn_from_file')"
      modal-class="spso-modal"
      hide-header-close
      hide-footer
      centered
      size="lg"
      @hidden="hideModal"
    >
      <div class="my-4">
        <div>
          <!-- eslint-disable max-len -->
          <button
            :disabled="loading"
            class="btn spso-button spso-button_line spso-button_lowercase shipment-notice-load__btn"
            @click="downloadTemplate"
          >
            <v-icon>mdi-download</v-icon>
            {{ $t('views.shipment_notice_list.download_template') }}
          </button>
          <!-- eslint-enable max-len -->
          <a
            v-if="template"
            v-show="false"
            ref="template"
            :href="template"
            download="asn-template.xlsx"
          />
          <!-- eslint-disable max-len -->
          <button
            :disabled="loading || submitted"
            class="btn spso-button spso-button_line spso-button_lowercase shipment-notice-load__btn"
            @click="selectFiles"
          >
            {{ $t('views.shipment_notice_list.select_files') }}
          </button>
          <!-- eslint-enable max-len -->
          <input
            v-show="false"
            ref="file"
            type="file"
            accept=".xlsx"
            multiple
            @change="setFiles"
          >
        </div>
      </div>

      <div
        v-if="files.length"
        class="shipment-notice-load__filelist my-3"
      >
        <div
          v-for="(file, i) in files"
          :key="i"
          class="shipment-notice-load__filename d-flex py-1"
        >
          <div class="shipment-notice-load__filename-text">
            {{ file.name }}
          </div>
          <v-spacer />
          <v-progress-circular
            v-if="file.status && file.status === 'pending'"
            size="24"
            class="ml-auto mr-1"
            width="2"
            indeterminate
          />
          <div>
            <v-icon
              v-if="file.status && file.status === 'rejected'"
              class="ml-auto"
              :class="statuses.errors.class"
              @click="showErrors(file)"
            >
              {{ statuses.errors.icon }}
            </v-icon>
            <v-icon
              v-if="file.status && file.status !== 'pending'"
              class="ml-auto"
              :class="statuses[file.status].class"
            >
              {{ statuses[file.status].icon }}
            </v-icon>
          </div>
        </div>
      </div>
      <div
        v-if="!submitted"
        class="v-application d-flex"
      >
        <v-btn
          :disabled="loading"
          outlined
          large
          class="mr-4 flex-grow-1"
          @click="hideModal"
        >
          {{ isUploadFinished ? $t('common.close') : $t('common.cancel') }}
        </v-btn>
        <v-btn
          :disabled="loading || !files.length"
          color="primary"
          outlined
          large
          class="flex-grow-1"
          @click="uploadFiles"
        >
          <v-icon>mdi-upload</v-icon>
          {{ $t('common.upload') }}
        </v-btn>
      </div>
      <div
        v-else
        class="v-application d-flex"
      >
        <v-btn
          :disabled="loading"
          color="primary"
          outlined
          large
          class="flex-grow-1"
          @click="hideModal"
        >
          {{ $t('common.close') }}
        </v-btn>
      </div>
    </b-modal>
    <shipment-notice-errors-modal
      v-model="isErrorsModalShown"
      :error-list="errorsModalList"
      :title="$t('views.shipment_notice_edit.validation_errors')"
    />
  </div>
</template>

<script>
import asyncPool from 'tiny-async-pool';
import ShipmentNoticeErrorsModal from '@/components/asn/ShipmentNoticeErrorsModal.vue';

export default {
  name: 'ShipmentNoticeLoad',

  components: {
    ShipmentNoticeErrorsModal,
  },

  model: {
    prop: 'value',
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
  },

  data() {
    return {
      isShown: false,
      template: null,
      files: [],
      loading: false,
      submitted: false,
      isErrorsModalShown: false,
      errorsModalList: [],
      processedFiles: [],
      isUploadFinished: false,
      statuses: {
        pending: {
          icon: 'mdi-loading',
          class: 'pending-icon',
        },
        created: {
          icon: 'mdi-check-circle-outline',
          class: 'success-icon',
        },
        rejected: {
          icon: 'mdi-cancel',
          class: 'error-icon',
        },
        errors: {
          icon: 'mdi-alert-box',
          class: 'error-icon',
        },
      },
    };
  },

  watch: {
    value(val) {
      this.isShown = val;

      if (val) {
        this.files = [];
        this.processedFiles = [];
        this.errorsModalList = [];
        this.isUploadFinished = false;
        this.submitted = false;
      }
    },
  },
  methods: {
    async downloadTemplate() {
      if (!this.template) {
        this.loading = true;
        try {
          const { data } = await this.$http.get('/asn/asn-template.xlsx', {
            headers: {
              'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            },
            responseType: 'blob',
          });
          if (data) {
            this.template = URL.createObjectURL(data);
            await this.$nextTick();
            this.$refs.template.click();
          }
        } catch (err) {
          this.$http.mockApiError(err);
        } finally {
          this.loading = false;
        }
      } else {
        this.$refs.template.click();
      }
    },

    showErrors(file) {
      const locale = this.$i18n.locale || 'en';
      const { translatedErrorList, response = {}, message } = file.error;
      const { data = {} } = response;
      const error = data.message || message;
      this.errorsModalList = translatedErrorList || [{ [locale]: error }];
      this.isErrorsModalShown = true;
    },

    hideModal() {
      this.$emit('input', false);
    },

    selectFiles() {
      this.$refs.file.value = null;
      this.$refs.file.click();
    },

    setFiles() {
      this.isUploadFinished = false;
      this.files = Array.from(this.$refs.file.files).map((it) => ({
        file: it,
        status: null,
        name: it.name,
      }));
    },

    async uploadFiles() {
      try {
        this.loading = true;
        this.submitted = true;
        this.files.forEach((it, i) => {
          this.$set(this.files, i, {
            ...it,
            status: 'pending',
          });
        });
        for await (const value of asyncPool(10, this.files, this.uploadFile)) {} //eslint-disable-line

        this.files = this.files
          .slice(0)
          .sort((a) => (a.status === 'rejected' ? -1 : 1));

        if (this.$refs.file) {
          this.$refs.file.value = null;
        }
      } finally {
        this.loading = false;
        this.isUploadFinished = true;
      }
    },

    async uploadFile({ file }) {
      const index = this.files.findIndex(({ name }) => file.name === name);
      try {
        const formData = new FormData();
        formData.append('file', file);

        await this.$http.post('/asn/create-from-xlsx', formData);
        this.$set(this.files, index, {
          ...this.files[index],
          status: 'created',
          error: null,
        });
      } catch (error) {
        this.$set(this.files, index, {
          ...this.files[index],
          status: 'rejected',
          error,
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shipment-notice-load__btn {
  padding: 10px 30px;
  margin-right: 20px;
}
.shipment-notice-load__filelist {
  max-height: 40vh;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    background-color: #fff;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--amber);
    border-radius: 10px;
  }
}
.shipment-notice-load__filename {
  color: var(--topaz);
  align-items: start;
  overflow: hidden;
}
.shipment-notice-load__filename-text {
  width: 520px;
  overflow-wrap: break-word;
}
</style>

<style lang="scss">
.success-icon {
  color: #4CAF50 !important;
}

.error-icon {
  color: #FF5252 !important;
}
</style>
